import { useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { Dialog, useAction } from "../../../components/ui";
import { loadMahnleiter, openDealsOnDefault, setDefaultCommission, updateSettings, type SettingsPatch } from "./data";
import { KonditionenForm, type KondTeil } from "./forms";
import { bpToPercentText } from "./model";
import { SettingsFrame } from "./parts";
import { konditionenZiel, type SeiteKey } from "./baum";
import { t } from "../../../lib/sprache";

/**
 * Früher „Einstellungen · Konditionen“ mit 16 Abschnitten. Seit IA-1 (21.10.2026) hat jedes Thema eine eigene Seite in „Alle
 * Einstellungen“; der alte Weg leitet weiter: bekannte Anker auf ihre Seite, sonst zur Gruppe Geld & Abrechnung.
 */
export function Konditionen() {
  const { hash } = useLocation();
  return <Navigate to={konditionenZiel(hash)} replace />;
}

/** Neue Standardprovision: laufende Kooperationen ohne eigenen Satz rechnen sonst ab sofort mit dem neuen Satz (Befund 15). */
interface Pending {
  values: SettingsPatch & { default_commission_bp: number };
  oldBp: number;
  count: number;
}

/**
 * Einzelseite aus den früheren Konditionen (Provision, Abrechnungsweg, Zahlungsziele, Prüfregeln, Wiedervorlagen,
 * Vergütungsmodelle, Rechnungsnummer). Gleicher Speicherweg wie bisher (updateSettings bzw. setDefaultCommission), gleicher
 * Abfrageschlüssel; die Rückfrage zur neuen Standardprovision bleibt.
 */
export function KonditionenTeil({ orgId, creator = false, teile, seite, title, text, kopf, darunter }: {
  orgId: string;
  creator?: boolean;
  teile: readonly KondTeil[];
  seite: SeiteKey;
  title: string;
  text?: ReactNode;
  /** ohne Seitenrahmen (Rechnungsnummer im Fenster der Nummernkreise) */
  kopf?: false;
  /** Inhalt unter dem Formular (z. B. Verweis auf verwandte Seiten) */
  darunter?: ReactNode;
}) {
  // Mahnleiter mitladen: rechnet eine Stufe in Werktagen, ist die Feiertagsregion Pflicht
  const q = useQuery({ queryKey: ["org_settings", orgId, "konditionen"], queryFn: () => loadMahnleiter(api, orgId) });
  const { busy, error, run } = useAction();
  const [pending, setPending] = useState<Pending | null>(null);
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending) return <Loading />;
  const d = q.data;

  async function save(v: SettingsPatch) {
    const oldBp = d.settings.default_commission_bp;
    const newBp = v.default_commission_bp;
    if (oldBp !== null && newBp !== null && newBp !== undefined && newBp !== oldBp) {
      const count = await openDealsOnDefault(api, orgId).catch(() => 0);
      if (count > 0) {
        setPending({ values: { ...v, default_commission_bp: newBp }, oldBp, count });
        return;
      }
    }
    // Seit 0039 ist der Satz je Kooperation beim Anlegen festgeschrieben: ein neuer Standard gilt nur für neue Kooperationen
    const changed = oldBp !== null && newBp !== null && newBp !== undefined && newBp !== oldBp;
    await run("konditionen", () => updateSettings(api, orgId, v),
      changed ? t("Konditionen gespeichert. Laufende Kooperationen behalten ihren festgeschriebenen Satz; der neue Standard gilt für neue Kooperationen.", "Terms saved. Ongoing deals keep their fixed rate; the new default applies to new deals.") : t("Gespeichert.", "Saved."));
  }
  async function decide(keepRunning: boolean) {
    if (!pending) return;
    const { default_commission_bp: bp, ...rest } = pending.values;
    const ok = await run("provision", async () => {
      const r = await setDefaultCommission(api, orgId, bp, keepRunning);
      await updateSettings(api, orgId, rest);
      return r;
    }, keepRunning
      ? t(`Konditionen gespeichert. ${pending.count === 1 ? t("1 laufende Kooperation rechnet", "1 ongoing deal continues") : t(`${pending.count} laufende Kooperationen rechnen`, `${pending.count} ongoing deals continue`)} weiter mit ${bpToPercentText(pending.oldBp)} %.`, `Terms saved. ${pending.count === 1 ? t("1 laufende Kooperation rechnet", "1 ongoing deal continues") : t(`${pending.count} laufende Kooperationen rechnen`, `${pending.count} ongoing deals continue`)} at ${bpToPercentText(pending.oldBp)} %.`)
      : t("Konditionen gespeichert. Laufende Kooperationen ohne eigenen Satz rechnen ab jetzt mit dem neuen Standard.", "Terms saved. Ongoing deals without their own rate now use the new default."));
    if (ok) setPending(null);
  }

  const formular = (
    <>
      <KonditionenForm
        key={d.settings.updated_at}
        settings={d.settings}
        defaults={d.defaults}
        steps={d.steps}
        creator={creator}
        strict={() => true}
        busy={!!busy}
        error={pending ? null : error}
        buttons={<button className="primary" type="submit" disabled={!!busy}>{busy ? t("Wird gespeichert …", "Saving …") : t("Speichern", "Save")}</button>}
        onSubmit={(v) => void save(v)}
        teile={teile}
      />
      {darunter}
      <Dialog open={!!pending} onClose={() => setPending(null)} title={t("Neue Standardprovision", "New default commission")}>
        {pending && (
          <div className="stack">
            <p>
              {t("Der Standard ändert sich von ", "The default changes from ")}{bpToPercentText(pending.oldBp)} {t("% auf ", "% to ")}{bpToPercentText(pending.values.default_commission_bp)} %.
              {pending.count === 1 ? t(" 1 laufende Kooperation rechnet", " 1 ongoing deal has used") : t(` ${pending.count} laufende Kooperationen rechnen`, ` ${pending.count} ongoing deals have used`)} {t("bisher mit dem Standard, weil weder die Kooperation noch der Creator einen eigenen Satz hat. Oft nennt der unterschriebene Vertrag den bisherigen Satz.", "the default so far because neither the deal nor the creator has its own rate. The signed contract often names the previous rate.")}</p>
            {error && <p className="tk-error" role="alert">{error}</p>}
            <div className="row">
              <button type="button" className="primary" disabled={!!busy} aria-busy={busy === "provision"} onClick={() => void decide(true)}>
                {t("Nur für neue Kooperationen", "Only for new deals")}</button>
              <button type="button" disabled={!!busy} onClick={() => void decide(false)}>{t("Auch für laufende", "Also for ongoing")}</button>
              <button type="button" onClick={() => setPending(null)}>{t("Abbrechen", "Cancel")}</button>
            </div>
            <small>{t("„Nur für neue Kooperationen“ trägt ", "“Only for new deals” enters ")}{bpToPercentText(pending.oldBp)} {t("% an diesen Kooperationen ein; neue Kooperationen bekommen den neuen Standard.", "% on these deals; new deals get the new default.")}</small>
          </div>
        )}
      </Dialog>
    </>
  );
  if (kopf === false) return formular;
  return <SettingsFrame section="konditionen" seite={seite} orgId={orgId} title={title} text={text}>{formular}</SettingsFrame>;
}
